// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ActionEnvelope, ChatState, SessionState, StateAction} from '@microsoft/agent-host-protocol'

export type RecoveryTrigger = 'socket_close' | 'stale_connection' | 'manual_retry' | 'reload' | 'visibility_return'
export type RecoveryRole = 'writable' | 'passive' | 'read_only' | 'unknown'
export type RecoveryObservationError = 'flag_evaluation' | 'observer' | 'clock' | 'emission' | 'observation_capacity'

export interface RecoveryControls {
  readonly session_id: string
  readonly environment_id: string
  readonly chat_id: string
  readonly role: RecoveryRole
  readonly active_turn_id?: string
  readonly action_mode: 'send' | 'steer' | 'queue' | 'blocked'
  readonly input_editable: boolean
  readonly send_enabled_for_valid_input: boolean
  readonly stop_present: boolean
}

export interface RecoveryRuntimeState {
  readonly clientId?: string
  readonly ownedTools: ReadonlyArray<{toolCallId: string; turnId: string}>
  readonly readyTools: readonly string[]
  readonly inventoryComplete: boolean
}

export interface RelayRecoveryContext {
  readonly taskId: string
  readonly sessionId: string
  readonly environmentId: string
  readonly generation: number
  snapshot(): {
    state?: ChatState
    sessionState?: SessionState
    sessionResource?: string
    passive: boolean
    passiveKnown: boolean
    hasConnected: boolean
  }
}

export interface RelayRecoveryObserver {
  adopt(context: RelayRecoveryContext): boolean
  select(viewId: string, surface: string): () => void
  controls(viewId: string, controls: RecoveryControls): void
  interrupted(trigger: RecoveryTrigger, generation: number, state: RecoveryRuntimeState): void
  attempt(generation: number): void
  credentials(generation: number): void
  initiated(generation: number): void
  handshake(generation: number, clientId: string, meta: unknown, path: 'snapshot' | 'replay' | 'unknown'): void
  session(resource: string): void
  replay(actions: readonly ActionEnvelope[]): (() => void) | undefined
  snapshot(state: ChatState): (() => void) | undefined
  action(envelope: ActionEnvelope): (() => void) | undefined
  command(channel: string, action: StateAction, clientId: string, clientSeq: number): void
  preparing(id: string): () => void
  clientToolLost(toolCallId: string, turnId: string): void
  coverageLost(reason: 'observation_capacity' | 'entry_unverified'): void
  unavailable(reason: 'disposed' | 'view_detached' | 'controls_unobserved' | 'visibility_interrupted'): void
  dispose(): void
}

let factory: ((context: RelayRecoveryContext) => RelayRecoveryObserver) | undefined
let reported: Set<RecoveryObservationError> | undefined

export function reportClientRecoveryError(reason: RecoveryObservationError): void {
  if (reported?.has(reason)) return
  ;(reported ??= new Set()).add(reason)
  try {
    reportError(new Error(`Client recovery telemetry ${reason} failed`), {
      critical: false,
      reactAppName: 'copilot-client-recovery',
    })
  } catch {
    // eslint-disable-next-line no-console
    console.error('Unable to report client recovery telemetry error')
  }
}

export function isClientRecoveryEnabled(): boolean {
  try {
    return isFeatureEnabled('one_copilot_chat')
  } catch {
    reportClientRecoveryError('flag_evaluation')
    return false
  }
}

export function observeClientRecovery<T>(operation: () => T): T | undefined {
  try {
    return operation()
  } catch {
    reportClientRecoveryError('observer')
    return undefined
  }
}

export function configureClientRecovery(next: (context: RelayRecoveryContext) => RelayRecoveryObserver): void {
  if (isClientRecoveryEnabled()) factory = next
}

export function createRelayClientRecovery(context: RelayRecoveryContext): RelayRecoveryObserver | undefined {
  if (!isClientRecoveryEnabled() || !factory) return
  return observeClientRecovery(() => factory?.(context))
}
